import React, { useEffect, type FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, FormInstance } from 'antd';
import { Space } from '@g2u/ui-kit';
import { isString } from 'lodash';

import { JsonDataItem, type WithOptionalDataTest } from 'types';
import { JobDetails, ServiceSelection } from 'pages/booking/location/components';
import { useProductList, useServiceSelection } from 'pages/booking/location/hooks';
import { BookingStore } from 'store';

export namespace EditDetails {
  export type Props = {
    onUpdate: (data: JsonDataItem) => void;
    onLoading: (state: boolean) => void;
    form: FormInstance;
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const { onUpdate, form, onLoading } = props;
    const { serviceSelection, setProductSelection, job, serviceList, productSelection } = BookingStore;
    const { isServiceListLoading, serviceListError, refetchServiceList } = useServiceSelection(null);
    const { isProductListLoading, productListError, getProductList } = useProductList(null, null);

    const isLoading = isProductListLoading || isServiceListLoading;

    useEffect(() => {
      onLoading(isLoading);
    }, [isLoading]);

    const handleFormChange = (field: JsonDataItem, fields: JsonDataItem) => {
      !isString(field.products) && setProductSelection({ ...productSelection, ...fields });
    };

    useEffect(() => {
      form.getFieldValue('products') &&
        !isString(form.getFieldValue('products')) &&
        setProductSelection({ ...productSelection, ...form.getFieldsValue() });
    }, [form.getFieldValue('products'), isServiceListLoading]);

    useEffect(() => {
      const preselectedDeviceIds = serviceSelection?.product_selection_raw.devices.map(
        (device: JsonDataItem) => device.id,
      );
      getProductList(form.getFieldsValue());
      setProductSelection({
        serviceType: serviceSelection?.service_attributes?.job_service_type,
        description: serviceSelection?.service_selection?.description,
        device: preselectedDeviceIds
          .map((id: number) => serviceList?.parent.options.find((option: JsonDataItem) => option.id === id)?.name)
          .filter(Boolean),
        os: serviceSelection?.service_selection_raw.os,
        products: serviceSelection?.product_selection_raw.products,
      });
      return () => onLoading(false);
    }, []);

    if (!productSelection) {
      return null;
    }

    return (
      <Form
        form={form}
        colon={false}
        layout="vertical"
        onValuesChange={handleFormChange}
        onFinish={onUpdate}
        style={{ width: '100%' }}
      >
        <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical} fullWidth>
          <JobDetails.$
            form={form}
            customerId={job?.customer_ID}
            onChange={() => getProductList(form.getFieldsValue())}
          />
          <ServiceSelection.$
            form={form}
            isLoading={isProductListLoading || isServiceListLoading}
            productListError={productListError}
            refetchProductList={() => getProductList(form.getFieldsValue())}
            serviceSelectionError={serviceListError}
            refetchServiceSelection={refetchServiceList}
          />
        </Space.$>
      </Form>
    );
  });
}
